import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {FocusEvent, KeyboardEvent, useLayoutEffect, useRef} from 'react';

import {isEmpty} from 'ramda';
import {noop} from 'ramda-adjunct';

import {assignSafely, getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {TimeType} from '../../../types/TimeType';
import {Chip} from '../../Chip/Chip';
import {ResolvedRelativeTime} from '../types/RelativeTime';

interface TimePickerRelativeTimesProps extends TestIdProps {
  relativeTimes: ResolvedRelativeTime[];
  value: TimeType | null;
  onSelect: (relativeTime: ResolvedRelativeTime) => void;
  focusedIndex?: number;
  onFocusChange?: (index: number) => void;
  onBlur?: () => void;
  onEscapeKey?: () => void;
  isFocused?: boolean;
}

export function TimePickerRelativeTimes(props: TimePickerRelativeTimesProps) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);

  const relativeTimesLength = props.relativeTimes.length;
  const focusedChipIndex = props.focusedIndex ?? -1;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    match(event.key)
      .with('ArrowDown', () => {
        event.preventDefault();
        props.onFocusChange?.((focusedChipIndex + 1) % relativeTimesLength);
      })
      .with('ArrowUp', () => {
        event.preventDefault();
        props.onFocusChange?.(
          focusedChipIndex === 0 ? relativeTimesLength - 1 : focusedChipIndex - 1
        );
      })
      .with('Tab', () => {
        if (event.shiftKey) {
          event.preventDefault();
          props.onBlur?.();
        }
        // Tab without Shift moves on to the list (handled by the focus trap).
      })
      .with('Escape', () => {
        event.preventDefault();
        event.stopPropagation();
        props.onEscapeKey?.();
      })
      .otherwise(noop);
  };

  // Enter/Space commits are handled by Chip itself (its handleKeyDown fires onChange);
  // adding a chip-level Enter/Space handler here would commit twice per keypress.

  // Focus the correct chip when focus index changes (layout effect so input blur sees
  // relatedTarget) — same mechanism as DatePickerRelativeDates.
  useLayoutEffect(() => {
    if (focusedChipIndex >= 0) {
      getSafely(chipRefs.current, focusedChipIndex)?.focus();
    }
  }, [focusedChipIndex]);

  if (isEmpty(props.relativeTimes)) {
    return null;
  }

  const handleContainerFocus = () => {
    // Focus the first chip when the container receives focus via Tab.
    if (focusedChipIndex === -1) {
      props.onFocusChange?.(0);
    }
  };

  const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    // Reset the focus index when leaving the container entirely.
    if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) {
      props.onBlur?.();
    }
  };

  return (
    <div
      onKeyDown={handleKeyDown}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
      tabIndex={props.isFocused ? 0 : -1}
      role="group"
      aria-label="Quick time selection options"
      aria-describedby="relative-times-instructions"
    >
      <div
        id="relative-times-instructions"
        className="absolute -m-px h-px w-px overflow-hidden border-0 p-0 whitespace-nowrap [clip:rect(0px,0px,0px,0px)]"
        aria-live="polite"
      >
        Use up and down arrow keys to navigate between time options, Enter or Space to select
      </div>
      <VStack spacing={3} data-testid={suffixTestId('relativeTimesContainer', props)}>
        {props.relativeTimes.map((relativeTime, index) => {
          const isSelected = props.value === relativeTime.time;
          const ariaLabel = `${relativeTime.label}${isSelected ? ', selected' : ', not selected'}`;

          return (
            <Chip
              key={`${relativeTime.time}-${relativeTime.label}`}
              ref={(element) => {
                assignSafely(chipRefs.current, index, element);
              }}
              label={relativeTime.label}
              suffix={relativeTime.suffix}
              value={isSelected}
              size="small"
              onChange={() => props.onSelect(relativeTime)}
              isFocused={focusedChipIndex === index}
              tabIndex={focusedChipIndex === index ? 0 : -1}
              ariaLabel={ariaLabel}
              data-testid={suffixTestId(`relativeTime-${relativeTime.time}`, props)}
            />
          );
        })}
      </VStack>
    </div>
  );
}
